import { MessageInternalNoteKind } from "@redotech/crdb-enums/shopper-ai/message-internal-note-kind";
import { MessageReactionName } from "@redotech/redo-agents-cards/support-agents/message-reaction";
import { Schema } from "effect";
import { MongoUserId } from "../../../../ids/mongo-ids";
import { enumLiterals, NullableDateFromDb } from "../../../../schema/helpers";
import { EmailAddressEntry } from "./email";
import {
  MessageFacebookCommentAttribute,
  MessageFacebookCommentState,
} from "./facebook-comment";
import {
  MessageInstagramCommentAttribute,
  MessageInstagramCommentState,
} from "./instagram-comment";
import {
  MessageMetaCommentAttribute,
  MessageMetaCommentState,
  MessageMetaCommentType,
} from "./meta-comment";
import { MessageRedoChatConfirmationState } from "./redo-chat";
import { StoredMessageRichContent } from "./rich-content";
import { MessageSmsService } from "./sms";
import { NewUiComponent, NewUiComponentResult } from "./ui-component";

/**
 * Max length of HTML stored inline in the `parsed_html_body` column. Email
 * HTML at or below this size is persisted directly in the DB (reply threads
 * and all); larger bodies are offloaded to S3 and referenced by `htmlBodyUrl`.
 * Guards the DB-write path (the repo models); the read path is uncapped so it
 * can materialize the full S3 body back into `parsedHtmlBody`.
 */
export const MAX_PARSED_HTML_BODY_LENGTH = 64 * 1024;

// Gmail channel data
export const GmailChannelData = Schema.Struct({
  _tag: Schema.Literal("gmail"),
  gmailMessageId: Schema.String,
  gmailHistoryId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * S3 URL to the full HTML body, populated ONLY when the HTML exceeds
   * MAX_PARSED_HTML_BODY_LENGTH and is stored in S3 instead of inline. When
   * set, S3 is the source of truth; when null, `parsedHtmlBody` holds the
   * full HTML.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Full HTML body of the email (reply threads included), stored inline when
   * it fits within MAX_PARSED_HTML_BODY_LENGTH; null when offloaded to S3 (see
   * `htmlBodyUrl`). On read this is the materialized full body (uncapped here
   * so the S3 source of truth can be inlined). Plain text for display/search
   * is derived separately by the email body parser.
   */
  parsedHtmlBody: Schema.NullOr(Schema.String),
  /**
   * Email addresses associated with this message (from, to, cc, bcc).
   */
  addresses: Schema.Array(EmailAddressEntry),
});
export type GmailChannelData = typeof GmailChannelData.Type;

// Outlook channel data
export const OutlookChannelData = Schema.Struct({
  _tag: Schema.Literal("outlook"),
  outlookMessageId: Schema.String,
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /**
   * S3 URL to the full HTML body, populated ONLY when the HTML exceeds
   * MAX_PARSED_HTML_BODY_LENGTH and is stored in S3 instead of inline. When
   * set, S3 is the source of truth; when null, `parsedHtmlBody` holds the
   * full HTML.
   */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  /**
   * Full HTML body of the email (reply threads included), stored inline when
   * it fits within MAX_PARSED_HTML_BODY_LENGTH; null when offloaded to S3 (see
   * `htmlBodyUrl`). On read this is the materialized full body (uncapped here
   * so the S3 source of truth can be inlined). Plain text for display/search
   * is derived separately by the email body parser.
   */
  parsedHtmlBody: Schema.NullOr(Schema.String),
  /**
   * Email addresses associated with this message (from, to, cc, bcc).
   */
  addresses: Schema.Array(EmailAddressEntry),
});
export type OutlookChannelData = typeof OutlookChannelData.Type;

// Custom email channel data (Postmark-based SMTP)
export const CustomEmailChannelData = Schema.Struct({
  _tag: Schema.Literal("customEmail"),
  customEmailMessageId: Schema.NullOr(Schema.String),
  smtpMessageId: Schema.NullOr(Schema.String),
  headers: Schema.NullOr(Schema.Unknown),
  /** See {@link GmailChannelData} for the htmlBodyUrl / parsedHtmlBody contract. */
  htmlBodyUrl: Schema.NullOr(Schema.String.pipe(Schema.maxLength(2048))),
  parsedHtmlBody: Schema.NullOr(Schema.String),
  addresses: Schema.Array(EmailAddressEntry),
});
export type CustomEmailChannelData = typeof CustomEmailChannelData.Type;

// Email channel data - union of Gmail, Outlook, and CustomEmail
export const EmailChannelData = Schema.Union(
  GmailChannelData,
  OutlookChannelData,
  CustomEmailChannelData,
);
export type EmailChannelData = typeof EmailChannelData.Type;

// SMS channel data. Agent tapback `reaction` lives here (not on the base
// message) because tapbacks are an SMS/iMessage-channel concept; null when none.
export const SmsChannelData = Schema.Struct({
  _tag: Schema.Literal("sms"),
  messageService: MessageSmsService,
  reaction: Schema.optional(Schema.NullOr(Schema.Enums(MessageReactionName))),
  // Raw `postback_data` behind a tapped RCS chip; null/absent for typed replies.
  suggestionPostback: Schema.optional(Schema.NullOr(Schema.String)),
  // Set when read receipts key on something other than the external id.
  providerMessageId: Schema.optional(Schema.NullOr(Schema.String)),
  // When the customer's handset reported our message read; absent until then.
  readAt: Schema.optional(Schema.NullOr(Schema.Date)),
  // Cards/carousel/chips the message was sent as, tagged by format; null for text.
  richContent: Schema.optional(StoredMessageRichContent),
  // When the provider confirmed delivery (RCS finalized, iMessage gateway
  // result); absent for plain SMS and for a send only known to be accepted.
  deliveredAt: Schema.optional(Schema.NullOr(Schema.Date)),
});
export type SmsChannelData = typeof SmsChannelData.Type;

// TikTok channel data. The tiktok message id lives on `message.external_message_id`
// like Facebook DM, so no extra side-row fields are needed here.
export const TiktokChannelData = Schema.Struct({
  _tag: Schema.Literal("tiktok"),
});
export type TiktokChannelData = typeof TiktokChannelData.Type;

// Mobile message (iMessage) channel data. Backed by the same `message_sms`
// subtable as SMS, so the agent tapback `reaction` lives here too; null when none.
export const MobileMessageChannelData = Schema.Struct({
  _tag: Schema.Literal("mobileMessage"),
  reaction: Schema.optional(Schema.NullOr(Schema.Enums(MessageReactionName))),
  readAt: Schema.optional(Schema.NullOr(Schema.Date)),
  deliveredAt: Schema.optional(Schema.NullOr(Schema.Date)),
});
export type MobileMessageChannelData = typeof MobileMessageChannelData.Type;

// The PDP the customer had open when they sent the message; absent off a PDP.
export const ViewedProduct = Schema.Struct({
  productId: Schema.String,
  productName: Schema.String,
  variantId: Schema.optional(Schema.String),
  storefrontProductUrl: Schema.optional(Schema.String),
});
export type ViewedProduct = typeof ViewedProduct.Type;

// Redo Chat channel data
export const RedoChatChannelData = Schema.Struct({
  _tag: Schema.Literal("redoChat"),
  contentType: Schema.String,
  structuredContent: Schema.NullOr(Schema.Unknown),
  confirmationState: Schema.NullOr(MessageRedoChatConfirmationState),
  pendingToolCallId: Schema.NullOr(Schema.String),
  viewedProduct: Schema.optional(Schema.NullOr(ViewedProduct)),
});
export type RedoChatChannelData = typeof RedoChatChannelData.Type;

// Voice channel data
export const VoiceDirection = Schema.Literal("inbound", "outbound");
export type VoiceDirection = typeof VoiceDirection.Type;

export const VoiceStatus = Schema.Literal(
  "ringing",
  "answered",
  "missed",
  "voicemail",
  "completed",
  "failed",
);
export type VoiceStatus = typeof VoiceStatus.Type;

export const VoiceChannelData = Schema.Struct({
  _tag: Schema.Literal("voice"),
  direction: VoiceDirection,
  status: VoiceStatus,
  telnyxCallControlId: Schema.String,
  telnyxRecordingId: Schema.NullOr(Schema.String),
  recordingUrl: Schema.NullOr(Schema.String),
  transcriptUrl: Schema.NullOr(Schema.String),
  durationSeconds: Schema.NullOr(Schema.Number),
  startedAt: Schema.Date,
  connectedAt: Schema.NullOr(Schema.Date),
  endedAt: Schema.NullOr(Schema.Date),
});
export type VoiceChannelData = typeof VoiceChannelData.Type;

// Facebook comment channel data
export const FacebookCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("facebookComment"),
  state: Schema.NullOr(MessageFacebookCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageFacebookCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type FacebookCommentChannelData = typeof FacebookCommentChannelData.Type;

// Instagram comment channel data
export const InstagramCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("instagramComment"),
  state: Schema.NullOr(MessageInstagramCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageInstagramCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
export type InstagramCommentChannelData =
  typeof InstagramCommentChannelData.Type;

// Meta comment channel data (for facebook and instagram comments) - DEPRECATED
const MetaCommentChannelData = Schema.Struct({
  _tag: Schema.Literal("metaComment"),
  type: MessageMetaCommentType,
  state: Schema.NullOr(MessageMetaCommentState),
  attributes: Schema.NullOr(Schema.Array(MessageMetaCommentAttribute)),
  privateRepliedCommentId: Schema.NullOr(Schema.String),
  privateRepliedCommentText: Schema.NullOr(Schema.String),
  privateRepliedCommentPermalink: Schema.NullOr(Schema.String),
});
type MetaCommentChannelData = typeof MetaCommentChannelData.Type;

// Facebook DM channel data
export const FacebookDmChannelData = Schema.Struct({
  _tag: Schema.Literal("facebookDm"),
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
});
export type FacebookDmChannelData = typeof FacebookDmChannelData.Type;

// Instagram DM kind: set when the DM has a recognized Instagram-specific kind.
export const InstagramDmKind = Schema.Literal("mention", "reply");
export type InstagramDmKind = typeof InstagramDmKind.Type;

// Instagram DM channel data
export const InstagramDmChannelData = Schema.Struct({
  _tag: Schema.Literal("instagramDm"),
  isEcho: Schema.Boolean,
  isDeleted: Schema.Boolean,
  // Reconstructed from a nullable column: the read emits `kind: null` for
  // non-story DMs, so accept null (as absent) or the transcript decode fails.
  kind: Schema.optionalWith(InstagramDmKind, { nullable: true }),
});
export type InstagramDmChannelData = typeof InstagramDmChannelData.Type;

/** What wrote an internal note: a rep's `note`, or one of the platform's own. */
export const InternalNoteKind = Schema.Literal(
  ...enumLiterals(MessageInternalNoteKind),
);
export type InternalNoteKind = typeof InternalNoteKind.Type;

/** Notes the platform writes to start an AI run, which the run replies to. */
export const AiRunTriggerNoteKind = InternalNoteKind.pipe(
  Schema.pickLiteral("action_authorization", "authorize_shop_app"),
);
export type AiRunTriggerNoteKind = typeof AiRunTriggerNoteKind.Type;

// Internal note channel data
export const InternalNoteChannelData = Schema.Struct({
  _tag: Schema.Literal("internalNote"),
  htmlContent: Schema.NullOr(Schema.String),
  mentionedMongoUserIds: Schema.NullOr(Schema.Array(MongoUserId)),
  kind: Schema.optionalWith(InternalNoteKind, { default: () => "note" }),
  /** Whether the note goes into the AI agent's LLM context. */
  visibleToAi: Schema.optionalWith(Schema.Boolean, { default: () => true }),
});
export type InternalNoteChannelData = typeof InternalNoteChannelData.Type;

// Contact form custom field (merchant-defined fields beyond the standard set)
export const ContactFormCustomField = Schema.Struct({
  label: Schema.String,
  value: Schema.String,
});
export type ContactFormCustomField = typeof ContactFormCustomField.Type;

export const ContactFormSource = Schema.Literal("redo", "shopify");
export type ContactFormSource = typeof ContactFormSource.Type;

// Contact form channel data. `comment` lives on `message.content` — not
// duplicated here.
export const ContactFormChannelData = Schema.Struct({
  _tag: Schema.Literal("contactForm"),
  source: Schema.optionalWith(ContactFormSource, { default: () => "redo" }),
  name: Schema.NullOr(Schema.String),
  email: Schema.String,
  phone: Schema.NullOr(Schema.String),
  subject: Schema.NullOr(Schema.String),
  customFields: Schema.Array(ContactFormCustomField),
});
export type ContactFormChannelData = typeof ContactFormChannelData.Type;

// UI component channel data: the card a `uiComponent` message shows. Only a
// stored card has `expiredReason` and `decidedAt`.
export const UiComponentChannelData = Schema.extend(
  NewUiComponent,
  Schema.Struct({
    _tag: Schema.Literal("uiComponent"),
    expiredReason: Schema.optional(Schema.NullOr(Schema.String)),
    decidedAt: Schema.optional(NullableDateFromDb),
  }),
);
export type UiComponentChannelData = typeof UiComponentChannelData.Type;

// UI component result channel data: the button press a `uiComponentResult` message records.
export const UiComponentResultChannelData = Schema.extend(
  NewUiComponentResult,
  Schema.Struct({ _tag: Schema.Literal("uiComponentResult") }),
);
export type UiComponentResultChannelData =
  typeof UiComponentResultChannelData.Type;

// Discriminated union of all message channel data types
export const MessageChannelData = Schema.Union(
  GmailChannelData,
  OutlookChannelData,
  CustomEmailChannelData,
  SmsChannelData,
  TiktokChannelData,
  MobileMessageChannelData,
  RedoChatChannelData,
  VoiceChannelData,
  FacebookCommentChannelData,
  InstagramCommentChannelData,
  MetaCommentChannelData,
  FacebookDmChannelData,
  InstagramDmChannelData,
  InternalNoteChannelData,
  ContactFormChannelData,
  UiComponentChannelData,
  UiComponentResultChannelData,
);
export type MessageChannelData = typeof MessageChannelData.Type;
